import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import DataTable from "../components/admin/DataTable";
import PrimaryActionButton from "../components/admin/PrimaryActionButton";
import { EDITIONS } from "../config/adminConfigs";
import {
  addPage,
  deleteIssue,
  getIssueDetail,
  getPagePreviewUrl,
  listIssues,
  removePage,
  uploadIssue
} from "../services/epaperService";

/**
 * Local-timezone YYYY-MM-DD, unlike `new Date().toISOString().split("T")[0]`
 * which converts to UTC first — that silently shows "yesterday" as the
 * default date for any admin working in IST (UTC+5:30) between midnight
 * and 5:30 AM local time, which is exactly when a newspaper's morning
 * edition tends to get uploaded.
 */
const todayLocalDateKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Review / edit panel for one already-uploaded issue: lets an admin preview
 * each page's PDF inline, remove a page, or insert a new page at a chosen
 * position — without re-uploading the whole issue.
 */
const IssueReviewModal = ({ issue, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activePage, setActivePage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [removingPage, setRemovingPage] = useState(null);
  const [addFile, setAddFile] = useState(null);
  const [addPosition, setAddPosition] = useState("");
  const [isAddingPage, setAddingPage] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const nextDetail = await getIssueDetail({
        edition: issue.edition,
        dateKey: issue.id,
        type: issue.type
      });
      setDetail(nextDetail);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not load issue pages");
    } finally {
      setLoading(false);
    }
  }, [issue.edition, issue.id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const openPreview = async (page) => {
    try {
      setActivePage(page);
      setPreviewLoading(true);
      setPreviewUrl("");
      setError("");
      const url = await getPagePreviewUrl({
        edition: issue.edition,
        dateKey: issue.id,
        page,
        type: issue.type
      });
      setPreviewUrl(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not load page preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const onRemovePage = async (page) => {
    if (!window.confirm(`Remove page ${page} from this issue? This cannot be undone.`)) return;
    try {
      setRemovingPage(page);
      setError("");
      await removePage({
        edition: issue.edition,
        dateKey: issue.id,
        page,
        type: issue.type
      });
      if (activePage === page) {
        setActivePage(null);
        setPreviewUrl("");
      }
      await loadDetail();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not remove page");
    } finally {
      setRemovingPage(null);
    }
  };

  const onAddPage = async (event) => {
    event.preventDefault();
    if (!addFile || isAddingPage) return;
    try {
      setAddingPage(true);
      setError("");
      await addPage({
        edition: issue.edition,
        dateKey: issue.id,
        file: addFile,
        position: addPosition ? Number(addPosition) : undefined,
        type: issue.type
      });
      setAddFile(null);
      setAddPosition("");
      await loadDetail();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not add page");
    } finally {
      setAddingPage(false);
    }
  };

  const totalPages = detail?.pages?.length || 0;

  return (
    <div className="modal-overlay epaper-review-overlay">
      <div className="epaper-review-shell">
        <div className="epaper-review-header">
          <h2>Review &amp; Edit — {issue.title} ({issue.date})</h2>
          <button type="button" className="ghost-btn" onClick={onClose}>✕ Close</button>
        </div>

        {error && <p className="error-banner">{error}</p>}

        <div className="epaper-review-body">
          <div className="epaper-review-pages">
            <h4>Pages ({totalPages})</h4>
            {loading ? (
              <p className="loading-text">Loading pages...</p>
            ) : (
              <ul className="epaper-page-list">
                {(detail?.pages || []).map((pageEntry) => (
                  <li
                    key={pageEntry.page}
                    className={activePage === pageEntry.page ? "epaper-page-item active" : "epaper-page-item"}
                  >
                    <button
                      type="button"
                      className="epaper-page-select-btn"
                      onClick={() => openPreview(pageEntry.page)}
                    >
                      Page {pageEntry.page}
                    </button>
                    <button
                      type="button"
                      className="delete-btn"
                      title="Remove this page"
                      disabled={removingPage === pageEntry.page}
                      onClick={() => onRemovePage(pageEntry.page)}
                    >
                      {removingPage === pageEntry.page ? "…" : "✖"}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <form className="epaper-add-page-form" onSubmit={onAddPage}>
              <h4>Add Page</h4>
              <label>
                PDF Page *
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event) => setAddFile(event.target.files?.[0] || null)}
                  required
                />
              </label>
              <label>
                Insert at position (optional)
                <input
                  type="number"
                  min="1"
                  max={totalPages + 1}
                  placeholder={`Defaults to end (${totalPages + 1})`}
                  value={addPosition}
                  onChange={(event) => setAddPosition(event.target.value)}
                />
              </label>
              <button type="submit" className="primary-action-btn" disabled={!addFile || isAddingPage}>
                {isAddingPage ? "Adding..." : "Add Page"}
              </button>
            </form>
          </div>

          <div className="epaper-review-preview">
            <h4>Preview{activePage ? ` — Page ${activePage}` : ""}</h4>
            {!activePage && <p className="loading-text">Select a page to preview it here.</p>}
            {activePage && previewLoading && <p className="loading-text">Loading preview...</p>}
            {activePage && !previewLoading && previewUrl && (
              <iframe
                key={previewUrl}
                src={previewUrl}
                title={`Page ${activePage} preview`}
                className="epaper-preview-frame"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const EpaperAdminPage = ({ module }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedEdition, setSelectedEdition] = useState(module.defaultEdition || EDITIONS[0]);
  const [dateKey, setDateKey] = useState(todayLocalDateKey);
  const [issueType, setIssueType] = useState("main");
  const [subscriptionRequired, setSubscriptionRequired] = useState(true);
  const [files, setFiles] = useState([]);
  const [isUploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reviewingIssue, setReviewingIssue] = useState(null);

  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
      const issues = await listIssues(selectedEdition);
      setRows(issues);
      setError("");
    } catch (err) {
      setError(err.message || "Could not load e-paper issues");
    } finally {
      setLoading(false);
    }
  }, [selectedEdition]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const onUpload = async (event) => {
    event.preventDefault();
    if (!files.length || isUploading) return;

    try {
      setUploading(true);
      setError("");
      setNotice("");
      setProgress(0);
      await uploadIssue({
        edition: selectedEdition,
        dateKey,
        files,
        type: issueType,
        subscriptionRequired,
        onProgress: setProgress
      });
      setFiles([]);
      setNotice(`Uploaded ${files.length} page(s) for ${selectedEdition} (${dateKey}).`);
      await fetchIssues();
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDelete = useCallback(async (row) => {
    if (!window.confirm(`Delete issue ${row.date}?`)) return;
    try {
      setError("");
      setNotice("");
      await deleteIssue({
        edition: selectedEdition,
        dateKey: row.id,
        type: row.type
      });
      setNotice(`Deleted issue ${row.id}.`);
      await fetchIssues();
    } catch (err) {
      setError(err.message || "Delete failed");
    }
  }, [fetchIssues, selectedEdition]);

  const columns = useMemo(
    () => [
      {
        key: "index",
        label: "#",
        accessor: () => "",
        searchable: false,
        render: (_row, index) => index
      },
      { key: "title", label: "Title", accessor: "title" },
      { key: "date", label: "Date", accessor: "date" },
      {
        key: "type",
        label: "Type",
        accessor: "type",
        render: (row) => <span className={`epaper-type ${row.type}`}>{row.type}</span>
      },
      { key: "subscription", label: "Subscription Required", accessor: "subscriptionRequired" },
      { key: "pages", label: "Total Pages", accessor: "totalPages" },
      {
        key: "actions",
        label: "Actions",
        accessor: () => "",
        sortable: false,
        searchable: false,
        render: (row) => (
          <div className="table-actions">
            <button type="button" className="icon-btn" onClick={() => setReviewingIssue(row)} title="Review / edit pages">
              👁
            </button>
            <button type="button" className="delete-btn" onClick={() => onDelete(row)} title="Delete issue">
              ✖
            </button>
          </div>
        )
      }
    ],
    [onDelete, setReviewingIssue]
  );

  return (
    <AdminLayout module={module} title={module.pageTitle}>
      {error && <p className="error-banner">{error}</p>}
      {notice && <p className="warning-banner">{notice}</p>}
      <div className="toolbar-row">
        <PrimaryActionButton label={module.primaryActionLabel} onClick={() => setNotice("")} />
      </div>
      <section className="upload-card">
        <h3>Upload E-Paper Issue</h3>
        <form className="epaper-upload-form" onSubmit={onUpload}>
          <label>
            Edition *
            <select value={selectedEdition} onChange={(event) => setSelectedEdition(event.target.value)} required>
              {EDITIONS.map((edition) => (
                <option key={edition} value={edition}>
                  {edition}
                </option>
              ))}
            </select>
          </label>
          <label>
            Date *
            <input type="date" value={dateKey} onChange={(event) => setDateKey(event.target.value)} required />
          </label>
          <label>
            Type
            <select value={issueType} onChange={(event) => setIssueType(event.target.value)}>
              <option value="main">main</option>
              <option value="purti">purti</option>
            </select>
          </label>
          <label>
            Subscription Required
            <select
              value={subscriptionRequired ? "yes" : "no"}
              onChange={(event) => setSubscriptionRequired(event.target.value === "yes")}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="epaper-file-field">
            PDF Pages *
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
              required
            />
          </label>
          <button type="submit" className="primary-action-btn" disabled={isUploading || !files.length}>
            {isUploading ? "Uploading..." : "Upload Issue"}
          </button>
        </form>
        {isUploading && (
          <p className="loading-text">Upload progress: {Math.round(progress * 100)}% ({files.length} page PDFs)</p>
        )}
      </section>
      {loading ? <p className="loading-text">Loading e-paper issues...</p> : <DataTable columns={columns} rows={rows} />}

      {reviewingIssue && (
        <IssueReviewModal
          issue={reviewingIssue}
          onClose={() => setReviewingIssue(null)}
          onChanged={fetchIssues}
        />
      )}
    </AdminLayout>
  );
};

export default EpaperAdminPage;
